import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { LandingNavbar } from '../components/LandingNavbar';
import Footer from '../components/Footer';

/**
 * AccountDeletionPage
 * 
 * A clean, modern fintech-style page for requesting account deletion via email.
 * Adheres to "Savvy Community" branding requirements.
 * NOW INCLUDES: Navbar and Footer for professional trust signals.
 */
const AccountDeletionPage = ({ onBack }) => {
  // Brand color: Deep Burgundy (Savvy Community primary)
  const PRIMARY_COLOR = '#800b33';

  // Handle "Back to app" click
  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Navigation Bar */}
      <LandingNavbar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
        }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', p: 0 }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: '680px', // ~640-720px range
              borderRadius: 4,
              p: { xs: 3, md: 5, lg: 6 },
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              bgcolor: '#ffffff',
            }}
          >
            {/* Top Label */}
            <Chip
              label="Account & Data"
              size="small"
              sx={{
                mb: 3,
                bgcolor: 'rgba(128, 11, 51, 0.08)',
                color: PRIMARY_COLOR,
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.05em'
              }}
            />

            {/* Page Title */}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
              Account Deletion Request – Savvy Community
            </Typography>

            {/* Intro Paragraph */}
            <Typography variant="body1" sx={{ color: '#64748b', fontSize: '1.05rem', lineHeight: 1.6, mb: 4 }}>
              If you would like to delete your Savvy Community account, you can submit a deletion request at any time.
            </Typography>

            <Divider sx={{ my: 4, borderColor: '#f1f5f9' }} />

            {/* Section 1 - How to request */}
            <Box component="section" sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', mb: 2 }}>
                How to request account deletion
              </Typography>

              <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
                Please send an email with the details below to our support team:
              </Typography>

              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                href="mailto:charlesnicholaus12@gmail.com"
                sx={{
                  mb: 3,
                  color: PRIMARY_COLOR,
                  borderColor: 'rgba(128, 11, 51, 0.3)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  '&:hover': {
                    borderColor: PRIMARY_COLOR,
                    bgcolor: 'rgba(128, 11, 51, 0.04)',
                  }
                }}
              >
                charlesnicholaus12@gmail.com
              </Button>

              {/* Email Format Box */}
              <Box
                sx={{
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  p: 3
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
                  Subject: <Box component="span" sx={{ fontWeight: 400 }}>Delete My Savvy Community Account</Box>
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mt: 2, mb: 1 }}>
                  Include in the email:
                </Typography>

                <List dense disablePadding>
                  {[
                    'Your registered phone number or email',
                    'Your full name',
                    'Group name (if applicable)'
                  ].map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                      <Box component="span" sx={{ color: PRIMARY_COLOR, mr: 1.5, fontSize: '1.2rem', lineHeight: 1 }}>•</Box>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2', color: '#475569' }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: '#f1f5f9' }} />

            {/* Section 2 - What happens after */}
            <Box component="section" sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', mb: 2 }}>
                What happens after you request deletion
              </Typography>

              <List sx={{ pl: 0 }}>
                {[
                  'Your account login will be disabled and scheduled for deletion.',
                  'Your personal profile data (name, email, phone number, login credentials and settings) will be permanently deleted or anonymized within 7 days.',
                  'Financial and transaction records (such as contributions, loans, repayments, penalties and group statements) may be retained as required by law and for audit purposes, but they will no longer be linked to your personal identity wherever possible.',
                  'If you have any questions about account deletion, you can contact us at: charlesnicholaus12@gmail.com.'
                ].map((text, i) => (
                  <ListItem key={i} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                    <Box sx={{ minWidth: 6, width: 6, height: 6, borderRadius: '50%', bgcolor: '#94a3b8', mt: 1, mr: 2 }} />
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{ variant: 'body2', color: '#475569', lineHeight: 1.6 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Call to Action Area */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 4 }}>
              {onBack ? (
                <Button
                  onClick={handleBack}
                  variant="text"
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { color: '#334155', bgcolor: 'transparent' }
                  }}
                >
                  Back to app
                </Button>
              ) : (
                <Button
                  href="/"
                  variant="text"
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { color: '#334155', bgcolor: 'transparent' }
                  }}
                >
                  Back home
                </Button>
              )}

              <Button
                variant="contained"
                href="mailto:charlesnicholaus12@gmail.com?subject=Delete%20My%20Savvy%20Community%20Account"
                sx={{
                  bgcolor: PRIMARY_COLOR,
                  color: '#fff',
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(128, 11, 51, 0.25)',
                  '&:hover': {
                    bgcolor: '#5c0620',
                    boxShadow: '0 6px 16px rgba(128, 11, 51, 0.35)',
                  }
                }}
              >
                Email deletion request
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default AccountDeletionPage;
